import request from '@utils/axios-utils';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { CostCenter, ApiObject, CommonHash } from '@types';
import Notification from '@components/notification/notification';
import { useAuth } from '@providers/auth';

export function useCostCenters() {
  const { session } = useAuth();
  return useQuery<ApiObject<CostCenter[]>>(
    'cost_centers',
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: `/cost_centers`,
        method: 'GET',
      });
      return response.data;
    },
    { enabled: session.status != 'loading' && !!session.activeAccount?.cost_centers_available },
  );
}

export function useCreateCostCenter() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation(
    ({ data }: CommonHash) =>
      request({
        authorizationToken: session?.token,
        url: `/cost_centers`,
        method: 'POST',
        data,
      }),
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Plan de cuenta creado.' });
        queryClient.invalidateQueries('cost_centers');
      },
    },
  );
}

export function useAssignCostCenter() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation(
    ({ uuid, cost_center_id }: { uuid: string; cost_center_id: number | null }) =>
      request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}/assign_cost_center`,
        method: 'PATCH',
        data: { cost_center_id },
      }),
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Plan de cuenta asignado.' });
        queryClient.invalidateQueries(['documents']);
      },
    },
  );
}

export function useBulkAssignCostCenter() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation(
    ({ uuids, cost_center_id }: { uuids: string[]; cost_center_id: number | null }) =>
      request({
        authorizationToken: session?.token,
        url: `/documents/bulk_assign_cost_center`,
        method: 'POST',
        data: { uuids, cost_center_id },
      }),
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Planes de cuenta asignados.' });
        queryClient.invalidateQueries(['documents']);
      },
    },
  );
}

export function useUpdateCostCenter() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation(
    ({ id, name }: { id: number; name: string }) =>
      request({
        authorizationToken: session?.token,
        url: `/cost_centers/${id}`,
        method: 'PATCH',
        data: { cost_center: { name } },
      }),
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Plan de cuenta actualizado.' });
        queryClient.invalidateQueries('cost_centers');
      },
    },
  );
}

export function useDiscardCostCenter() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation(
    (id: number) =>
      request({
        authorizationToken: session?.token,
        url: `/cost_centers/${id}`,
        method: 'DELETE',
      }),
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Plan de cuenta desactivado.' });
        queryClient.invalidateQueries('cost_centers');
      },
    },
  );
}
